import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'

// Growth progress store (capability growth integration).
//
// Composes two backend reads:
//   • /sites/:id/capabilities/growth — per-capability coverage + engagement
//     progress computed from the append-only social metrics history.
//   • /sites/:id/growth-dashboard    — the existing site-wide channel
//     envelope (traffic / seo / social referral / leads) with its
//     no-fabricated-numbers status vocabulary. Reused, not recomputed.
// The channel overview failing never blocks the capability half — each has
// isolated state. Hardening mirrors analyticsWorkspace.js: newest-response-
// wins sequencing and classified errors.
export const useGrowthProgressStore = defineStore('growthProgress', () => {
  const growth = ref(null)
  const loading = ref(false)
  const error = ref(null)

  const overview = ref(null)
  const overviewLoading = ref(false)
  const overviewError = ref(null)

  const announce = ref('')

  let growthSeq = 0
  let overviewSeq = 0

  const capabilities = computed(() => growth.value?.capabilities || [])
  const summary = computed(() => growth.value?.summary || { total: 0, covered: 0, partial: 0, uncovered: 0 })
  const social = computed(() => growth.value?.social || null)

  // Capabilities with measured engagement float to the top, biggest reach first.
  const rankedCapabilities = computed(() =>
    [...capabilities.value].sort((a, b) =>
      (b.engagement?.totals?.impressions ?? -1) - (a.engagement?.totals?.impressions ?? -1)))

  const uiState = computed(() => {
    if (loading.value) return 'loading'
    if (error.value) return 'error'
    if (!capabilities.value.length) return 'empty'
    return 'ready'
  })

  function classifyError(err) {
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return { kind: 'timeout', message: 'The request timed out. Check your connection and retry.' }
    }
    if (!err?.response) {
      return { kind: 'network', message: 'Network error — you appear to be offline. Retry when reconnected.' }
    }
    if (err.response.status >= 500) {
      return { kind: 'server', message: 'Growth data is temporarily unavailable. Please retry shortly.' }
    }
    return { kind: 'generic', message: err.response?.data?.error || 'Something went wrong. Please retry.' }
  }

  async function loadGrowth(siteId) {
    const seq = ++growthSeq
    loading.value = true
    error.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/capabilities/growth`)
      if (seq !== growthSeq) return
      growth.value = data.growth || null
      announce.value = 'Growth progress updated'
    } catch (err) {
      if (seq !== growthSeq) return
      error.value = classifyError(err).message
      announce.value = 'Failed to load growth progress'
    } finally {
      if (seq === growthSeq) loading.value = false
    }
  }

  async function loadOverview(siteId, window = '30d') {
    const seq = ++overviewSeq
    overviewLoading.value = true
    overviewError.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/growth-dashboard`, { params: { window } })
      if (seq !== overviewSeq) return
      overview.value = data || null
    } catch (err) {
      if (seq !== overviewSeq) return
      overviewError.value = classifyError(err).message
    } finally {
      if (seq === overviewSeq) overviewLoading.value = false
    }
  }

  // The two halves load independently — a dead channel overview never blocks
  // capability progress, and vice versa.
  async function loadAll(siteId) {
    await Promise.all([loadGrowth(siteId), loadOverview(siteId)])
  }

  return {
    growth, loading, error,
    overview, overviewLoading, overviewError,
    announce,
    capabilities, rankedCapabilities, summary, social, uiState,
    loadGrowth, loadOverview, loadAll,
  }
})
